import React, { useEffect, useState } from 'react'
import PatternRow from './PatternRow'
import { defaultPattern, Pattern, usePatternContext } from '../contexts/PatternContext'
import { Box, Typography, Paper, CircularProgress, Button } from '@mui/material'
import { styled } from '@mui/material/styles'
import { usePatternSidePanelContext } from '../contexts/PatternSidePanelContext'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { useSavePattern } from '../../../hooks/useSavePattern'
import { useTutorialContext } from '../../../contexts/TutorialContext'
import { createTutorialMockPattern } from '../../../tour/TutorialMockData'

const PatternListContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  overflow: 'auto',
  backgroundColor: theme.palette.background.custom1,
  padding: theme.spacing(0, 1),
}))

const PatternList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 0),
}))

const NoPatternMessage = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  textAlign: 'center',
  background: theme.palette.background.custom1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  elevation: 0,
  boxShadow: 'none',
}))

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  padding: theme.spacing(4),
}))

const SectionHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0.5),
  paddingBottom: theme.spacing(0.5),
  backgroundColor: theme.palette.background.custom1,
}))

const compareByName = (a: Pattern, b: Pattern) =>
  (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })

function PatternListView() {
  const { tradingHouse, usermeta, canViewPrivatePatterns } = useApplicationSuiteContext()
  const { isTutorialActive, activeTutorialType } = useTutorialContext()

  const {
    patterns: originalPatterns,
    patternsLoading,
    patternsError,
    tradingHousePatterns,
    tradingHousePatternsLoading,
    tradingHousePatternsError,
    setEditingPattern,
    setEditingPatternIsTradingHouse,
  } = usePatternContext()
  const { selectedPattern } = usePatternSidePanelContext()
  const [patterns, setPatterns] = useState<Pattern[]>([])

  // Setup save pattern functionality
  const { forceSave } = useSavePattern(
    (pattern) => {},
    usermeta,
    tradingHouse,
    canViewPrivatePatterns
  )

  // Update patterns array when originals change or when selectedPattern is updated
  useEffect(() => {
    if (!originalPatterns) {
      setPatterns([])
      return
    }

    const tutorialDemoPattern = createTutorialMockPattern(tradingHouse)

    let updatedPatterns = [...originalPatterns]

    if (selectedPattern && selectedPattern.id) {
      updatedPatterns = originalPatterns.map((p) =>
        p.id === selectedPattern.id ? selectedPattern : p
      )
    }

    if (isTutorialActive && activeTutorialType === 'patternBuilder') {
      setPatterns([tutorialDemoPattern, ...updatedPatterns])
    } else {
      setPatterns(updatedPatterns)
    }
  }, [originalPatterns, selectedPattern, tradingHouse, isTutorialActive, activeTutorialType])

  const createPattern = async () => {
    try {
      const newPattern = {
        ...defaultPattern,
        tradingHouse: tradingHouse,
      }
      const savedPattern = await forceSave(newPattern)
      if (savedPattern) {
        setEditingPattern(savedPattern)
      }
    } catch (error) {
      console.error('Error creating new pattern:', error)
    }
  }

  const handleEditPattern = (pattern: Pattern, isTradingHouse?: boolean) => {
    setEditingPatternIsTradingHouse(!!isTradingHouse)
    setEditingPattern(pattern)
  }

  if (patternsLoading || tradingHousePatternsLoading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    )
  }

  if (patternsError || tradingHousePatternsError) {
    return (
      <NoPatternMessage>
        <Typography variant="h6" color="error">
          Error loading patterns
        </Typography>
        <Typography color="text.secondary">
          {(patternsError || tradingHousePatternsError)?.message || 'An unknown error occurred.'}
        </Typography>
      </NoPatternMessage>
    )
  }

  const tradingHouseProperName = tradingHouse === 'gtf' ? 'Day Trade U' : 'GreenChart'
  const hasUserPatterns = patterns.length > 0
  const hasTradingHousePatterns = tradingHousePatterns.length > 0

  return (
    <PatternListContainer id="pattern-card-list-container">
      {/* Trading House Patterns Section */}
      {hasTradingHousePatterns && (
        <>
          <SectionHeader>
            <Typography
              variant="overline"
              fontWeight="700"
              color="text.secondary"
              data-tour-id="trading-house-patterns-section"
              sx={{ letterSpacing: '0.08em', fontSize: '0.7rem' }}
            >
              {tradingHouseProperName} Patterns
            </Typography>
          </SectionHeader>
          <PatternList>
            {[...tradingHousePatterns].sort(compareByName).map((pattern: Pattern) => (
              <PatternRow
                key={`th-${pattern.id}`}
                pattern={pattern}
                isTradingHousePattern
                onEdit={(p) => handleEditPattern(p, true)}
              />
            ))}
          </PatternList>
        </>
      )}

      <SectionHeader>
        <Typography
          variant="overline"
          fontWeight="700"
          color="text.secondary"
          data-tour-id="your-patterns-section"
          sx={{ letterSpacing: '0.08em', fontSize: '0.7rem' }}
        >
          Your Patterns
        </Typography>
      </SectionHeader>

      {/* User Patterns Section */}
      {hasUserPatterns ? (
        <PatternList>
          {[...patterns]
            .sort((a, b) => (b.starred ? 1 : 0) - (a.starred ? 1 : 0) || compareByName(a, b))
            .map((pattern: Pattern) => (
              <PatternRow
                key={pattern.id}
                pattern={pattern}
                onEdit={(p) => handleEditPattern(p, false)}
              />
            ))}
        </PatternList>
      ) : (
        <NoPatternMessage>
          <Typography variant="h6">No Patterns Found</Typography>
          <Typography color="text.secondary" maxWidth="600px">
            Patterns are a way to organize your trading ideas. They are a collection of criteria
            that you use to identify trade entries. Create a new pattern to get started.
          </Typography>
          <Button variant="contained" color="primary" onClick={createPattern}>
            Create New Pattern
          </Button>
        </NoPatternMessage>
      )}
    </PatternListContainer>
  )
}

export default PatternListView
